/**
 * Pure rules for the Connect AI consent page (AVA-560): which state a failed call leads to, what a
 * failed decision tells the user, and the copy facts that come from server configuration.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */

/**
 * Lifetime of an approved assistant session, in days. Mirrors the API's JwtOptions defaults
 * (aiva.api MODS.Forge.Api/Options/JwtOptions.cs): the refresh token slides to RefreshTokenDaysMcp
 * on every rotation, and AbsoluteSessionExpiryDays caps the session from the day it was approved.
 * Keep in step with the API until it returns these values with the request.
 */
export const MCP_SESSION_LIMITS = Object.freeze({ idleDays: 30, maxDays: 90 });

/** Page states a failed call can lead to. */
export const CONNECT_AI_STATE = Object.freeze({
  EXPIRED: 'expired',
  LOAD_FAILED: 'load-failed',
  ASK: 'ask',
});

/**
 * HTTP status of an axios-style error, or null when no response came back (network, CORS, timeout).
 *
 * @param {unknown} err
 * @returns {number|null}
 */
export function httpStatusOf(err) {
  const status = err && err.response && err.response.status;
  return typeof status === 'number' ? status : null;
}

/**
 * The API answers 404 (`request_expired`) when the pending request is gone: it expired, or it was
 * already approved or denied. Every other failure is temporary as far as the page can tell.
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function isRequestGone(err) {
  return httpStatusOf(err) === 404;
}

/**
 * The state to show when loading the request failed.
 *
 * @param {unknown} err
 * @returns {'expired'|'load-failed'}
 */
export function loadFailureState(err) {
  return isRequestGone(err) ? CONNECT_AI_STATE.EXPIRED : CONNECT_AI_STATE.LOAD_FAILED;
}

/**
 * What a failed approve / deny leads to. A gone request ends the page; anything else keeps the
 * question open. A 400 carries the API's own reason (impersonating, signed in with an assistant
 * token), which retrying cannot fix, so it is shown instead of the generic "try again" text.
 *
 * @param {unknown} err
 * @returns {{ state: 'expired'|'ask', message: string|null }}
 */
export function decisionFailure(err) {
  if (isRequestGone(err)) return { state: CONNECT_AI_STATE.EXPIRED, message: null };
  if (httpStatusOf(err) !== 400) return { state: CONNECT_AI_STATE.ASK, message: null };
  const data = err.response.data || {};
  const message = typeof data.message === 'string' ? data.message : data.Message;
  return { state: CONNECT_AI_STATE.ASK, message: typeof message === 'string' && message.trim() ? message.trim() : null };
}

/**
 * First questions suggested once access is approved. Uses the user's current facility when the
 * app knows it, otherwise questions that work without one.
 *
 * @param {unknown} facilityName
 * @returns {string[]}
 */
export function exampleQuestions(facilityName) {
  const facility = typeof facilityName === 'string' ? facilityName.trim() : '';
  if (!facility) {
    return [
      'Which facilities can I open in AVA?',
      'Which anomalies are still open, by facility?',
      'Make a dashboard of open tasks by status.',
    ];
  }
  return [
    `What data do we have for ${facility}?`,
    `Which anomalies are still open in ${facility}?`,
    'Make a dashboard of open tasks by status.',
  ];
}
